import {
  centsToRatio,
  clamp01,
  createAdvancedVoice,
  createModalBank,
  createNoiseBurst,
  hashSeed
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ModalSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

function jangguModes(head: "low" | "high", variantAttack: number): ModalSpec[] {
  if (head === "low") {
    return [
      { ratio: 1, gain: 0.22, decay: 0.22 },
      { ratio: 1.43, gain: 0.12, decay: 0.16, detuneCents: -8 },
      { ratio: 1.92, gain: 0.08, decay: 0.12 },
      { ratio: 2.7, gain: 0.04, decay: 0.08 },
      { ratio: 3.6, gain: 0.026, decay: 0.055 }
    ];
  }

  return [
    { ratio: 1, gain: 0.16, decay: 0.095 - variantAttack * 0.02 },
    { ratio: 1.58, gain: 0.11, decay: 0.07 },
    { ratio: 2.26, gain: 0.09, decay: 0.052 },
    { ratio: 3.12, gain: 0.06, decay: 0.04 },
    { ratio: 4.58, gain: 0.036, decay: 0.032 },
    { ratio: 6.1, gain: 0.024, decay: 0.024 }
  ];
}

export function renderJanggu(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("janggu", input.variantId);
  const highStroke = input.ornament === "chaepyeon" || input.ornament === "rim" || input.noteLabel === "G4";
  const blocked = input.ornament === "muted" || input.ornament === "blocked";
  const head: "low" | "high" = highStroke ? "high" : "low";
  const baseFrequency = head === "low" ? 118 + (variant?.bodyGain ?? 0.6) * 65 : 480 + (variant?.brightness ?? 0.6) * 420;
  const gain = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain];
  const modal = createModalBank(context, baseFrequency, jangguModes(head, variant?.attack ?? 0.6), {
    destination: gain,
    velocity: input.velocity,
    now: input.now,
    family: "membrane_modal",
    quality: input.quality,
    totalGain: blocked ? 0.56 : 0.82,
    duration: blocked ? 0.24 : 0.75
  });
  const clickFrequency = head === "low" ? 780 : input.ornament === "rim" ? 3400 : 2100;
  const clickGain = input.velocity * (head === "low" ? 0.045 : 0.12) * (variant?.noiseGain ?? 0.45);
  const attackNoise = createNoiseBurst(context, {
    duration: head === "low" ? 0.05 : 0.032,
    attack: 0.0007,
    decay: head === "low" ? 1.7 : 2.8,
    gain: clickGain,
    filterType: "bandpass",
    frequency: clickFrequency,
    q: head === "low" ? 1.0 : 2.6,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, input.ornament, "janggu-click"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = input.velocity * (0.8 + (variant?.bodyGain ?? 0.5) * 0.12);
  attackNoise.output.connect(gain);
  attackNoise.source.start(input.now);
  sources.push(...modal.sources, attackNoise.source);
  cleanupNodes.push(...modal.cleanupNodes, ...attackNoise.cleanupNodes);

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: blocked ? 0.035 : head === "low" ? 0.16 : 0.08,
    debug: {
      family: "membrane_modal",
      model: "janggu_split_head_modal",
      head,
      layers: [head === "low" ? "gungpyeon_low_modal" : "chaepyeon_high_modal", "stroke_transient", blocked ? "blocked_damping" : "open_decay"]
    }
  });
}

function tablaModes(head: "dayan" | "bayan", pressure: number, bol: string | undefined): ModalSpec[] {
  if (head === "bayan") {
    const pressureShift = pressure * 0.28;

    return [
      { ratio: 1 + pressureShift, gain: 0.24, decay: 0.28 + pressure * 0.16 },
      { ratio: 1.48 + pressureShift * 0.5, gain: 0.11, decay: 0.17 + pressure * 0.08 },
      { ratio: 2.18 + pressureShift * 0.3, gain: 0.07, decay: 0.11 },
      { ratio: 3.04, gain: 0.04, decay: 0.07 }
    ];
  }

  const bright = bol === "na" || bol === "ta" || bol === "muted";
  const sustain = bol === "tin" || bol === "tun";

  return [
    { ratio: 1, gain: sustain ? 0.2 : 0.15, decay: sustain ? 0.42 : 0.22 },
    { ratio: 2.01, gain: 0.12, decay: sustain ? 0.34 : 0.18, detuneCents: 3 },
    { ratio: 3.03, gain: 0.09, decay: sustain ? 0.26 : 0.13, detuneCents: -4 },
    { ratio: 4.04, gain: 0.058, decay: sustain ? 0.19 : 0.09, detuneCents: 5 },
    { ratio: 5.08, gain: bright ? 0.052 : 0.032, decay: sustain ? 0.14 : 0.06, detuneCents: -7 },
    { ratio: 6.72, gain: bright ? 0.032 : 0.018, decay: 0.045 }
  ];
}

function resolveTablaBol(input: AdvancedSynthesisInput): { head: "dayan" | "bayan"; bol: string } {
  const ornament = input.ornament ?? "";

  if (ornament === "bayan_pressure" || ornament === "ge" || ornament === "ghe" || input.noteLabel === "C4") {
    return { head: "bayan", bol: ornament || "ge" };
  }

  if (ornament === "muted") {
    return { head: "dayan", bol: "ta" };
  }

  if (ornament === "dayan" || ornament === "tin" || ornament === "tun") {
    return { head: "dayan", bol: ornament };
  }

  return { head: "dayan", bol: "na" };
}

export function renderTabla(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("tabla", input.variantId);
  const pressure = input.ornament === "bayan_pressure" ? controlNumber(input, "bayanPressure", 0.72) : 0;
  const { head, bol } = resolveTablaBol(input);
  const baseFrequency = head === "bayan" ? 108 : input.frequency;
  const gain = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain];
  const modes = tablaModes(head, pressure, bol);
  const modal = createModalBank(context, baseFrequency, modes, {
    destination: gain,
    velocity: input.velocity,
    now: input.now,
    family: "membrane_modal",
    quality: input.quality,
    totalGain: head === "bayan" ? 0.88 : 0.78,
    duration: head === "bayan" ? 1.1 : 0.95
  });
  const click = createNoiseBurst(context, {
    duration: head === "bayan" ? 0.055 : bol === "tin" || bol === "tun" ? 0.035 : 0.024,
    attack: 0.0007,
    decay: bol === "na" || bol === "ta" ? 3.1 : 1.9,
    gain: input.velocity * (head === "bayan" ? 0.035 : 0.075) * (variant?.noiseGain ?? 0.45),
    filterType: "bandpass",
    frequency: head === "bayan" ? 420 : bol === "na" || bol === "ta" ? 3200 : 1600,
    q: head === "bayan" ? 0.9 : 2.2,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, input.ornament, "tabla-click"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = input.velocity * (0.72 + (variant?.bodyGain ?? 0.65) * 0.18);
  click.output.connect(gain);
  click.source.start(input.now);
  sources.push(...modal.sources, click.source);
  cleanupNodes.push(...modal.cleanupNodes, ...click.cleanupNodes);

  if (head === "bayan" && pressure > 0) {
    for (const [index, source] of modal.sources.entries()) {
      const ratio = modes[index]?.ratio ?? 1;
      const base = baseFrequency * ratio;
      const bendRatio = centsToRatio(80 + pressure * 185);

      source.frequency.setValueAtTime(base, input.now);
      source.frequency.linearRampToValueAtTime(base * bendRatio, input.now + 0.16);
      source.frequency.linearRampToValueAtTime(base * (1 + pressure * 0.08), input.now + 0.36);
      source.frequency.linearRampToValueAtTime(base, input.now + 0.62);
    }
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: head === "bayan" ? 0.18 : 0.09,
    debug: {
      family: "membrane_modal",
      model: "tabla_dayan_bayan_modal_syahi",
      head,
      bol,
      pressure,
      layers: [head === "bayan" ? "bayan_pressure_modal" : "dayan_quasi_harmonic", "bol_excitation", "syahi_attack"]
    }
  });
}
